import { useState, useRef, useEffect, useMemo } from 'react';

import "./messaging-input-footer.css";
import { VscSend } from "react-icons/vsc";
import { MdPreview } from "react-icons/md";
import CountdownTimer from "../helpers/countdownTimer";
import { util } from "../helpers/common";
import { CONVERSATION_CONSTANTS, CLIENT_CONSTANTS, MAX_MESSAGE_LENGTH } from '../helpers/constants';
import { getConversationId } from '../services/dataProvider';
import { renderMarkdown, containsMarkdown } from '../helpers/markdownUtils';
import { useStrings } from '../i18n/useStrings';
import { formatString } from '../i18n/strings';
import FileUploadButton from './FileUploadButton';
import FilePreview from './FilePreview';
import { sendFile } from '../services/messagingService';
import { validateFile } from '../helpers/fileValidation';

export default function MessagingInputFooter(props) {
    const strings = useStrings();

    // Initialize the Textarea value to empty.
    const [textareaContent, setTextareaContent] = useState('');
    const [showMarkdownPreview, setShowMarkdownPreview] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploadingFiles, setIsUploadingFiles] = useState(false);
    const [fileError, setFileError] = useState(null);

    const textareaRef = useRef(null);
    const footerRef = useRef(null);
    const typingIndicatorTimer = useRef(null);
    const pendingBlobUrls = useRef(new Set()); // Track blob URLs for cleanup
    const pendingFilesLock = useRef(false); // Lock for sessionStorage operations

    // Re-adjust textarea height when returning from preview mode
    useEffect(() => {
        if (!showMarkdownPreview && textareaRef.current && textareaContent) {
            // Small delay to ensure textarea is mounted in DOM
            requestAnimationFrame(() => {
                if (textareaRef.current) {
                    textareaRef.current.style.height = 'auto';
                    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
                    
                    // Don't set fixed height on footerContainer - let it grow naturally
                }
            });
        }
    }, [showMarkdownPreview, textareaContent]);

    // Cleanup blob URLs when component unmounts
    useEffect(() => {
        return () => {
            // Revoke all tracked blob URLs
            pendingBlobUrls.current.forEach(url => {
                URL.revokeObjectURL(url);
            });
            pendingBlobUrls.current.clear();
        };
    }, []);

    /**
     * Handle 'change' event in Textarea to reactively update the Textarea value.
     * @param {object} event
     */
    function handleTextareaContentChange(event) {
        if (event && event.target && typeof event.target.value === "string") {
            const newValue = event.target.value;
            setTextareaContent(newValue);
            setFileError(null); // Clear any file errors when user starts typing

            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto'; // reset
                textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
            }

            // Don't set fixed height on footerContainer - let it grow naturally with content
            // The fixed height causes issues when FilePreview is shown

            // Typing indicator logic
            if (newValue.trim() !== "") {
                if (typingIndicatorTimer.current) {
                    typingIndicatorTimer.current.reset(Date.now());
                } else {
                    handleSendTypingIndicator(CONVERSATION_CONSTANTS.EntryTypes.TYPING_STARTED_INDICATOR);
                    typingIndicatorTimer.current = new CountdownTimer(() => {
                        handleSendTypingIndicator(CONVERSATION_CONSTANTS.EntryTypes.TYPING_STOPPED_INDICATOR);
                        typingIndicatorTimer.current = null;
                    }, CLIENT_CONSTANTS.TYPING_INDICATOR_DISPLAY_TIMEOUT, Date.now());
                    typingIndicatorTimer.current.start();
                }
            }
        }
    }


    /**
     * Handle 'key' event in Textarea. If the key is 'Enter', send a message.
     * @param {object} event
     */
    function handleTextareaKeyChange(event) {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();

            handleSendButtonClick();
        }
    }

    /**
     * Handle 'click' event in Textarea to put focus on Textarea.
     * @param {object} event
     */
    function handleTextareaClick(event) {
        if (event) {
            event.target.focus();
        }
    }

    /**
     * Clears the Textarea i.e. resets to empty.
     */
    function clearMessageContent() {
        setTextareaContent("");

        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    }

    /**
     * Determines whether to disable the Textarea.
     * TRUE - disables if the conversation is either closed or not started and FALSE - otherwise.
     */
    function shouldDisableTextarea() {
        return props.conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION || props.conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION;
    }

    /**
     * Determines whether to disable the Send Button.
     * TRUE - disables if the Textarea is either empty, message is too long, or if the conversation is not open and FALSE - otherwise.
     */
    const shouldDisableSendButton = useMemo(() => {
        // If files are selected, only check conversation status and upload state
        if (selectedFiles.length > 0) {
            return isUploadingFiles || props.conversationStatus !== CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION;
        }

        // For text messages, check text content as well
        return textareaContent.trim().length === 0 ||
               textareaContent.length > MAX_MESSAGE_LENGTH ||
               props.conversationStatus !== CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION;
    }, [textareaContent, selectedFiles, isUploadingFiles, props.conversationStatus]);

    /**
     * Handle Send Button click.
     * If files are selected, send files. Otherwise send text message.
     */
    function handleSendButtonClick() {
        if (selectedFiles.length > 0) {
            handleSendFiles();
        } else if (!shouldDisableSendButton) {
            handleSendMessage();
        }
    }

    /**
     * Handle sending a text message by generating a new unique message-id and invoke the parent's handler to send a message with the typed text.
     */
    function handleSendMessage() {
        // Required parameters.
        const conversationId = getConversationId();
        const messageId = util.generateUUID();
        let value = textareaContent;

        // Double-check: Should not send if message is too long (button should be disabled)
        // Note: Salesforce actual limit is 2500 chars based on real-world testing
        if (value.length > MAX_MESSAGE_LENGTH) {
            console.error(`[MessagingInputFooter] Cannot send message: ${value.length} characters exceeds maximum ${MAX_MESSAGE_LENGTH}`);
            return; // Abort sending
        }

        // Optional parameters.
        let inReplyToMessageId;
        let isNewMessagingSession;
        let routingAttributes;
        let language;

        props.sendTextMessage(conversationId, value, messageId, inReplyToMessageId, isNewMessagingSession, routingAttributes, language)
            .then(() => {
                // Return to edit mode if in preview mode
                if (showMarkdownPreview) {
                    setShowMarkdownPreview(false);
                }
                // Clear textarea value.
                clearMessageContent();
            });
    }

    /**
     * Handle calling a sendTypingIndicator when the timer fires with started/stopped indicator.
     * @param {string} typingIndicator - whether to send a typing started or stopped indicator.
     */
        function handleSendTypingIndicator(typingIndicator) {
            const conversationId = getConversationId();

            props.sendTypingIndicator(conversationId, typingIndicator)
            .catch(error => {
                console.error(`Something went wrong while sending a typing indicator to conversation ${conversationId}: ${error}`);
            });
        }

    /**
     * Toggle Markdown preview mode
     */
    function toggleMarkdownPreview() {
        setShowMarkdownPreview(!showMarkdownPreview);
    }

    /**
     * Check if preview button should be shown (only if text contains Markdown)
     */
    function shouldShowPreviewButton() {
        return textareaContent.trim().length > 0 && containsMarkdown(textareaContent);
    }

    /**
     * Check if message exceeds maximum length
     * Note: Salesforce actual limit is much lower than documented
     * Using 2500 chars based on real-world testing
     */
    const isMessageTooLong = useMemo(() => {
        return textareaContent.length > MAX_MESSAGE_LENGTH;
    }, [textareaContent]);

    /**
     * Handle file selection from FileUploadButton
     * @param {Array<File>} files - Array of selected files
     */
    function handleFilesSelect(files) {
        setSelectedFiles(prevFiles => [...prevFiles, ...files]);
        setFileError(null); // Clear any previous errors
    }

    /**
     * Handle file validation errors
     * @param {string} error - Error message to display
     */
    function handleFileError(error) {
        setFileError(error);
    }

    /**
     * Handle removing a specific file from selection
     * @param {File} fileToRemove - File to remove
     */
    function handleRemoveFile(fileToRemove) {
        setSelectedFiles(prevFiles => 
            prevFiles.filter(file => file !== fileToRemove)
        );
        setFileError(null); // Clear error when removing files
    }

    /**
     * Handle paste event to capture images from clipboard
     * @param {ClipboardEvent} event - Paste event
     */
    function handlePaste(event) {
        const items = event.clipboardData?.items;
        if (!items) return;

        const files = [];
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            
            // Check if item is an image
            if (item.type.indexOf('image') !== -1) {
                let file = item.getAsFile();
                if (file) {
                    // Fix: Pasted images often lack proper filenames
                    // Create a new File object with explicit name and type
                    if (!file.name || file.name === 'image.png') {
                        const timestamp = Date.now();
                        const extension = file.type.split('/')[1] || 'png';
                        const filename = `pasted-image-${timestamp}.${extension}`;
                        file = new File([file], filename, { type: file.type });
                    }
                    
                    // Validate the pasted image
                    const validation = validateFile(file);
                    if (validation.valid) {
                        files.push(file);
                    } else {
                        // Show error for invalid pasted image
                        let errorMsg;
                        switch (validation.error) {
                            case 'FILE_TOO_LARGE':
                                errorMsg = formatString(
                                    strings.fileTooLarge,
                                    validation.details.sizeMB,
                                    validation.details.maxSizeMB
                                );
                                break;
                            case 'INVALID_FILE_TYPE':
                                errorMsg = formatString(
                                    strings.fileTypeNotSupported,
                                    validation.details.fileType,
                                    validation.details.supportedTypes
                                );
                                break;
                            default:
                                errorMsg = strings.fileUploadError;
                        }
                        setFileError(errorMsg);
                    }
                }
                // Prevent default browser behavior after processing image
                event.preventDefault();
            }
        }

        if (files.length > 0) {
            // Prevent duplicates by checking if file with same name/size already exists
            setSelectedFiles(prevFiles => {
                const newFiles = files.filter(newFile => 
                    !prevFiles.some(existing => 
                        existing.name === newFile.name && existing.size === newFile.size
                    )
                );
                return [...prevFiles, ...newFiles];
            });
        }
    }

    /**
     * Send selected files sequentially (one at a time)
     * Files are sent in order, waiting for each to complete before sending next
     * Text is sent only with the first file
     */
    async function handleSendFiles() {
        if (selectedFiles.length === 0 || isUploadingFiles) {
            return;
        }

        const conversationId = getConversationId();
        const text = textareaContent.trim() || undefined;

        setIsUploadingFiles(true);

        // Wait for any pending sessionStorage operations to complete
        const waitForLock = () => {
            return new Promise((resolve) => {
                const checkLock = () => {
                    if (!pendingFilesLock.current) {
                        resolve();
                    } else {
                        setTimeout(checkLock, 10);
                    }
                };
                checkLock();
            });
        };

        try {
            // Send files sequentially
            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i];
                const messageId = util.generateUUID();
                const fileId = util.generateUUID();
                
                // Only include text with first file
                const messageText = i === 0 ? text : undefined;
                
                // Create blob URL and track it
                const localUrl = URL.createObjectURL(file);
                pendingBlobUrls.current.add(localUrl);
                
                // Store file info temporarily for client-side display
                // This is needed because Salesforce SSE doesn't include attachment details
                const fileInfo = {
                    messageId,
                    fileId,
                    file: {
                        name: file.name,
                        type: file.type,
                        size: file.size,
                        // Create a local URL for preview (will be revoked after display)
                        localUrl
                    }
                };
                
                // Wait for lock and store in sessionStorage
                await waitForLock();
                pendingFilesLock.current = true;
                try {
                    const pendingFilesKey = `pendingFiles_${conversationId}`;
                    const existingPending = sessionStorage.getItem(pendingFilesKey);
                    const pendingFiles = existingPending ? JSON.parse(existingPending) : [];
                    pendingFiles.push(fileInfo);
                    sessionStorage.setItem(pendingFilesKey, JSON.stringify(pendingFiles));
                } finally {
                    pendingFilesLock.current = false;
                }
                
                // Send file and wait for completion
                await sendFile(conversationId, file, messageId, fileId, messageText);
            }

            // Clear files and text on success
            setSelectedFiles([]);
            clearMessageContent();
            setFileError(null); // Clear any errors on success
        } catch (error) {
            console.error('File upload failed:', error);
            
            // Cleanup: Revoke blob URLs and remove pending files from sessionStorage
            await waitForLock();
            pendingFilesLock.current = true;
            try {
                const pendingFilesKey = `pendingFiles_${conversationId}`;
                const existingPending = sessionStorage.getItem(pendingFilesKey);
                if (existingPending) {
                    try {
                        const pendingFiles = JSON.parse(existingPending);
                        // Revoke blob URLs
                        pendingFiles.forEach(pf => {
                            if (pf.file?.localUrl) {
                                URL.revokeObjectURL(pf.file.localUrl);
                                pendingBlobUrls.current.delete(pf.file.localUrl);
                            }
                        });
                    } catch (e) {
                        console.error('Error cleaning up pending files:', e);
                    }
                    sessionStorage.removeItem(pendingFilesKey);
                }
            } finally {
                pendingFilesLock.current = false;
            }
            
            // Specific error messages based on status using i18n
            let errorMessage = strings.fileUploadError;
            if (error.status === 415) {
                errorMessage = strings.fileUploadError415 || strings.fileUploadError;
            } else if (error.status === 413) {
                errorMessage = strings.fileUploadError413 || strings.fileUploadError;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setFileError(errorMessage);
        } finally {
            setIsUploadingFiles(false);
        }
    }

    return(
        <div className="messagingFooter">
            <div className="footerContainer" ref={footerRef}>
                {/* File preview above input */}
                {selectedFiles.length > 0 && (
                    <FilePreview
                        files={selectedFiles}
                        onRemove={handleRemoveFile}
                    />
                )}

                <div className="inputWrapper">
                    {!showMarkdownPreview ? (
                        <textarea
                            ref={textareaRef}
                            className="messagingInputTextarea"
                            placeholder={strings.inputPlaceholder}
                            value={textareaContent}
                            onChange={handleTextareaContentChange}
                            onKeyDown={handleTextareaKeyChange}
                            onClick={handleTextareaClick}
                            onPaste={handlePaste}
                            disabled={shouldDisableTextarea()}
                        />
                    ) : (
                        <div className="markdownPreviewContainer">
                            <div
                                className="markdownPreview"
                                dangerouslySetInnerHTML={{ __html: renderMarkdown(textareaContent) }}
                            />
                        </div>
                    )}
                    <div className="buttonGroup">
                        {/* File upload button */}
                        <FileUploadButton
                            onFilesSelect={handleFilesSelect}
                            onError={handleFileError}
                            disabled={shouldDisableTextarea() || isUploadingFiles}
                        />

                        {/* Preview button (only for markdown) */}
                        {shouldShowPreviewButton() && (
                            <button
                                className="previewButton"
                                onClick={toggleMarkdownPreview}
                                title={showMarkdownPreview ? strings.editButton : strings.previewButton}
                            >
                                <MdPreview className="previewButtonIcon" />
                            </button>
                        )}

                        {/* Send button */}
                        <button
                            className="sendButton"
                            onClick={handleSendButtonClick}
                            disabled={shouldDisableSendButton}
                        >
                            <VscSend className="sendButtonIcon" />
                        </button>
                    </div>
                </div>

                {/* Message length warning */}
                {isMessageTooLong && selectedFiles.length === 0 && (
                    <div className="truncationWarning">
                        ⚠️ {formatString(
                            strings.messageTooLong,
                            textareaContent.length.toLocaleString(),
                            MAX_MESSAGE_LENGTH.toLocaleString()
                        )}
                    </div>
                )}

                {/* File error warning */}
                {fileError && (
                    <div className="truncationWarning">
                        ⚠️ {fileError}
                    </div>
                )}

                {/* Upload progress indicator */}
                {isUploadingFiles && (
                    <div className="uploadingIndicator">
                        {strings.uploadingFile || 'Uploading files...'}
                    </div>
                )}
            </div>
        </div>
    );
}